import React, { useState, useRef, useEffect } from "react";
import html2canvas from "html2canvas";
import { processImages, initializeModel } from "../../lib/process";
import { useDropzone } from "react-dropzone";
import FeaturedSection from "./FeaturedSection";
import Footer from "../components/layouts/Footer";
import textimg from "./images/text.svg";
import setting from "./images/settings.svg";
import imagesetting from "./images/image.svg";
import testwhite from './images/testwhite.svg'
import imgwhite from './images/imgwhite.svg'
import settingwhite from './images/settingwhite.svg'
import plus from './images/plus.svg'
import reset from './images/reset.svg'
import resetleft from './images/resetleft.svg'
import downloadicon from './images/downloadicon.svg'
import { FiDownload } from "react-icons/fi";
import FourStepDesign from "./FourStepDesign";
import borderimge from "./images/borderimge.svg"
import { TiTick } from "react-icons/ti";
import AiTouchLoader from "../components/layouts/AiTouchLoader";

type TextLayer = {
  id: number;
  name: string;
  text: string;
  fontSize: number;
  color: string;
  opacity: number;
  rotation: number;
  horizontal: number;
  vertical: number;
  align: string;
  fontfamily: string;
};

const TextBehind = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [foregroundImage, setForegroundImage] = useState<string | null>(null);
  const [textLayers, setTextLayers] = useState<TextLayer[]>([]);
  const [selectedLayerId, setSelectedLayerId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [editorVisible, setEditorVisible] = useState(false);
  const [imageRotation, setImageRotation] = useState(0);
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1);
  const [activeTab, setActiveTab] = useState<'text' | 'image' | 'settings'>('text');
  const [aspectRatio, setAspectRatio] = useState<string>('4:3');
  const [defaultLayerId, setDefaultLayerId] = useState<number | null>(null);


  const previewRef = useRef<HTMLDivElement>(null);

  const aspectRatios: Record<string, string> = {
    original: "auto",
    "1:1": "1 / 1",
    "4:3": "4 / 3",
    "16:9": "16 / 9",
  };

  useEffect(() => {
    (async () => {
      await initializeModel();
    })();
  }, []);

  const handleFiles = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const objectURL = URL.createObjectURL(file);
    setOriginalImage(objectURL);
    setLoading(true);

    try {
      const result = await processImages([file]);
      if (result && result.length > 0) {
        const fileWithTransparentBG = result[0];
        const foregroundDataURL = URL.createObjectURL(fileWithTransparentBG);
        setForegroundImage(foregroundDataURL);
        setEditorVisible(true);
        const layerId = addNewTextLayer();
        setDefaultLayerId(layerId);
      }
    } catch (error) {
      console.error("Background removal failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => handleFiles(acceptedFiles);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png"] },
  });

  const handleDownload = async () => {
    if (previewRef.current) {
      const canvas = await html2canvas(previewRef.current, {
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = "text-behind-object.png";
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    }
  };

  const addNewTextLayer = () => {
    const newLayer: TextLayer = {
      id: Date.now(),
      name: `Layer ${textLayers.length + 1}`,
      text: "Text Behind image",
      fontSize: 48,
      color: "#ffffff",
      opacity: 1,
      rotation: 0,
      horizontal: 50,
      fontfamily: "poppins",
      vertical: 50,
      align: "center",
    };
    setTextLayers((prev) => [...prev, newLayer]);
    setSelectedLayerId(newLayer.id);
    return newLayer.id;
  };


  const removeSelectedLayer = () => {
    // Directly check if there is only one text layer left.
    if (textLayers.length <= 1) {
      // Optionally, provide feedback to the user that the last layer cannot be removed.
      alert("You cannot remove the last text layer.");
      return;
    }

    // Proceed with removing the selected layer.
    setTextLayers((prev) => prev.filter((l) => l.id !== selectedLayerId));

    // After removing, select the first available layer or null if none are left.
    // This part of the logic might need adjustment based on your desired UX.
    setSelectedLayerId(textLayers[0]?.id || null);
  };


  const updateLayer = (id: number, key: keyof TextLayer, value: any) => {
    setTextLayers((prev) =>
      prev.map((layer) =>
        layer.id === id ? { ...layer, [key]: value } : layer
      )
    );
  };

  const fixContentPositions = () => {
    setTextLayers((prev) =>
      prev.map((layer) => ({
        ...layer,
        horizontal: 50,
        vertical: 50,
      }))
    );
  };

  const rotateImage = (direction: 'positive' | 'negative') => {
    const allowedRotations = [0, 90, 180, 270, -90, -180, -270];
    let newRotation = direction === 'positive'
      ? imageRotation + 90
      : imageRotation - 90;

    // Normalize to keep in allowed range
    if (newRotation > 270) newRotation = 0;
    if (newRotation < -270) newRotation = 0;

    setImageRotation(newRotation);
  };

  const selectedLayer = textLayers.find(
    (layer) => layer.id === selectedLayerId
  );

  const enhanceImage = () => {
    setBrightness(1.2);
    setContrast(1.2);
  };

  const googleFontFamilies = [
    "Poppins", "Roboto", "Open Sans", "Lato", "Montserrat", "Raleway", "Oswald", "Merriweather", "PT Sans",
    "Ubuntu", "Rubik", "Bebas Neue", "Quicksand", "Fira Sans", "Work Sans", "Cabin", "Inconsolata", "Karla",
    "Exo 2", "Nunito", "Titillium Web", "Asap", "Questrial", "Source Sans Pro", "Josefin Sans", "Playfair Display",
    "Zilla Slab", "Oxygen", "DM Sans", "Barlow", "Manrope", "Kanit", "Mukta", "Hind", "Crimson Text",
    "Libre Baskerville", "Cairo", "Overpass", "Anton", "Archivo", "Signika", "Assistant", "Domine",
    "Varela Round", "Heebo", "Cormorant Garamond", "Teko", "Abel", "Bitter", "Catamaran", "Lora"
  ];

  return (
    <>
      <div className=" bg-white flex flex-col items-center justify-start md:p-8 p-2">
        <div className="absolute top-[7.5rem] md:block hidden left-[19rem] w-72 h-72 rounded-full  bg-gradient-to-r from-purple-200/70 to-purple-300/70 mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-[5rem] 
  right-[19rem] w-72 h-72 rounded-full bg-gradient-to-r from-yellow-200/70 to-yellow-300/70  mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <h1 className="text-3xl md:text-6xl text-left lg:text-6xl md:text-center font-bold tracking-tight mb-6 mt-7 ">

          Create <span className="text-purple-600 underline font-poppins">Text-Behind-Image</span> Designs
        </h1>
        <p className="text-[15px] md:text-center text-left md:text-xl text-[#242424] font-medium font-poppins max-w-2xl mx-auto  md:px-2 px-1">
          Create text-behind-image designs in seconds using AI. Unlimited downloads. 100% free to use. No sign-up required.
        </p>
        {!editorVisible && !loading && (
          <div
            {...getRootProps()}
            className="border-2 glowing-card md:mt-28 mt-9 font-poppins border-gray-300 w-full max-w-2xl flex flex-col items-center justify-center rounded-lg cursor-pointer hover:border-purple-400"
          >
            {/* 👇 Keep only ONE getRootProps wrapper here */}
            <input {...getInputProps()} className="hidden" /> {/* important: must render this input */}

            <div
              style={{
                backgroundImage: `url(${borderimge})`,
                backgroundSize: 'calc(100% - -0px) 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                padding: "27px",
              }}
              className="rounded-2xl lg:p-4 flex flex-col items-center justify-center cursor-pointer transition hover:border-[#645ade]"
            >
              <h2 className="text-[15px] lg:text-2xl text-center font-poppins font-bold pt-[10px] pb-[7px] px-4 text-[#242424] mb-4">
                Drag and drop an image{" "}
                <span className="text-[#645ade] font-poppins font-bold">or browse to upload.</span>
              </h2>

              <button
                type="button"
                className="bg-[#4646c6] font-poppins hover:bg-[#5050de] text-white font-semibold text-[12px] lg:text-lg px-6 py-3 rounded-full shadow mb-2 transition"
              >
                Upload your photo
              </button>

              <p className="text-[10px] font-poppins text-[#242424] font-semibold mb-3">
                File must be JPEG, JPG, PNG or WebP and up to 40MB
              </p>

              <div className="flex justify-center items-center">
                <div className="flex items-center text-[#f06dad] text-[14px] md:text-[15px] font-poppins md:mr-4">
                  <span className="bg-[#f06dad] font-poppins rounded-full text-white p-[0.5px] md:mr-2"><TiTick /></span> Free to use
                </div>
                <div className="flex items-center text-[#f06dad] text-[14px] md:text-[15px] font-poppins">
                  <span className="bg-[#f06dad] font-poppins rounded-full text-white p-[0.5px] md:mr-2"><TiTick /></span> No credit card required
                </div>
              </div>
            </div>
          </div>
        )}


        {loading && (
          <AiTouchLoader />
        )}


        {editorVisible && !loading && (
          <div className="mt-24 mb-20 flex flex-col  justify-center sm:h-[700px]  md:flex-row gap-8 w-full max-w-6xl">
            {/* Canvas preview */}
            <div
              ref={previewRef}
              className="w-full h-full bg-black   overflow-hidden"
              style={{
                aspectRatio: aspectRatios[aspectRatio],
                transform: `rotate(${imageRotation}deg)`,
                filter: `brightness(${brightness}) contrast(${contrast})`,
                borderRadius: "10px",
              }}
            >
              {originalImage && (
                <img
                  src={originalImage}
                  alt="Original"
                  className="absolute   inset-0 w-[100%] h-[100%] object-contain z-0"
                />
              )}
              {textLayers.map((layer) => (
                <div
                  key={layer.id}
                  style={{
                    position: "absolute",
                    top: `${layer.vertical}%`,
                    left: `${layer.horizontal}%`,
                    transform: `translate(-50%, -50%) rotate(${layer.rotation}deg)`,
                    fontSize: `${layer.fontSize}px`,
                    color: layer.color,
                    opacity: layer.opacity,
                    fontFamily: layer.fontfamily,
                    textAlign: layer.align as any,
                    cursor: "pointer",
                    whiteSpace: "nowrap", // As per the previous fix
                  }}
                  className={`z-10 ${selectedLayerId === layer.id ? "outline outline-1 outline-white" : ""}`}
                  onClick={() => setSelectedLayerId(layer.id)}
                >
                  {layer.text}
                </div>
              ))}

              {foregroundImage && (
                <img
                  src={foregroundImage}
                  alt="Foreground"
                  className="absolute inset-0 w-[100%] h-[100%]  object-contain z-20 pointer-events-none"
                />
              )}
            </div>

            {/* Control panel */}
            <div className="w-full md:w-1/3 flex flex-col gap-4 ">
              <div className="flex justify-between rounded-[10px] h-[36px] items-center px-1  bg-[#f4f4f5]">
                <button
                  className={`font-semibold font-poppins flex pr-8 pl-4 items-center h-[32px] rounded-[10px] ${activeTab === 'text' ? 'text-white bg-[#18181b] ' : 'text-gray-500'
                    }`}
                  onClick={() => {
                    setActiveTab('text');

                    // If nothing is selected, select the default layer
                    if (selectedLayerId === null && defaultLayerId !== null) {
                      setSelectedLayerId(defaultLayerId);
                    }
                  }}
                >
                  {activeTab === 'text' ? <img src={testwhite} className="mr-2 w-4 " alt="sss" /> : <img src={textimg} className="mr-1 w-4 " alt="sss" />
                  }   Text
                </button>
                <button
                  className={`font-semibold font-poppins flex pr-8 pl-4 items-center h-[32px] rounded-[10px] ${activeTab === 'image' ? 'text-white bg-[#18181b] ' : 'text-gray-500'
                    }`}
                  onClick={() => setActiveTab('image')}
                >
                  {activeTab === 'image' ? <img src={imgwhite} className="mr-2 w-4" alt="sss" /> : <img src={imagesetting} className="mr-1 w-4" alt="sss" />
                  }
                  Image
                </button>
                <button
                  className={`font-semibold font-poppins flex pr-8 pl-4 items-center h-[32px] rounded-[10px] ${activeTab === 'settings' ? 'text-white bg-[#18181b] ' : 'text-gray-500'
                    }`}
                  onClick={() => setActiveTab('settings')}
                >
                  {activeTab === 'settings' ? <img src={settingwhite} className="mr-2 w-4" alt="sss" /> : <img src={setting} className="mr-1 w-4" alt="sss" />
                  }
                  Settings
                </button>
              </div>

              {activeTab === 'text' && selectedLayer && (
                <div className="overflow-y-auto px-2">
                  <div className="flex flex-col gap-4 mt-2 ">
                    <button
                      onClick={addNewTextLayer}
                      className="bg-black text-white hover:bg-gray-800 py-2 flex items-center justify-center font-semibold h-[40px] rounded-[10px]"
                    >
                      <img src={plus} className="mr-2 font-poppins" /> Add Text
                    </button>

                    <input
                      type="text"
                      value={selectedLayer.text}
                      onChange={(e) => updateLayer(selectedLayer.id, "text", e.target.value)}
                      className="border-b border-gray-200 rounded-[10px] py-2 px-2"
                    />



                    <label className="font-semibold font-poppins mt-3">
                      Font Size{" "}
                    </label>
                    <input
                      type="number"
                      min={20}
                      max={200}
                      className="w-full border-2  -mt-2 border-[#e7e7ea] rounded-[10px] p-2"
                      value={selectedLayer.fontSize}
                      onChange={(e) => {
                        let value = Number(e.target.value);
                        if (value > 200) value = 200;
                        if (value < 20) value = 20;
                        updateLayer(selectedLayer.id, "fontSize", value);
                      }}
                    />

                    <label className="font-semibold font-poppins mt-3">
                      Font Family
                    </label>

                    <select
                      value={selectedLayer.fontfamily}
                      onChange={(e) => updateLayer(selectedLayer.id, "fontfamily", e.target.value)}
                      className="w-52 border-2 -mt-2 font-poppins border-[#e7e7ea] px-2 rounded-[10px] p-2"
                    >
                      {googleFontFamilies.map((font) => (
                        <option key={font} value={font} style={{ fontFamily: font }}>
                          {font}
                        </option>
                      ))}
                    </select>

                    <label className="font-semibold font-poppins">
                      Color
                    </label>
                    <input
                      style={{ borderRadius: "10px" }}
                      type="color"
                      value={selectedLayer.color}
                      className="w-full border-2 -mt-2 border-[#000]"
                      onChange={(e) => updateLayer(selectedLayer.id, "color", e.target.value)}
                    />





                    <label className="font-semibold mt-3 font-poppins">
                      Opacity
                    </label>
                    <input
                      style={{ background: "#000" }}
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      className="w-full -mt-2 rounded-[15px] "
                      value={selectedLayer.opacity}
                      onChange={(e) =>
                        updateLayer(selectedLayer.id, "opacity", parseFloat(e.target.value))
                      }
                    />




                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex-1">
                        <label className="font-semibold font-poppins">
                          Rotation
                        </label>
                        <input
                          style={{ background: "#000" }}
                          type="range"
                          min={-180}
                          max={180}
                          className="w-full -mt-2 rounded-[15px]"
                          value={selectedLayer.rotation}
                          onChange={(e) =>
                            updateLayer(
                              selectedLayer.id,
                              "rotation",
                              Number(e.target.value)
                            )
                          }
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          updateLayer(selectedLayer.id, "rotation", 0)
                        }
                        className="ml-2 mt-6"
                      >
                        <img src={reset} />
                      </button>
                    </div>



                    <label className="font-semibold mt-3 font-poppins">
                      Horizontal
                    </label>
                    <input
                      style={{ background: "#000" }}
                      type="range"
                      min={0}
                      className="w-full -mt-2 rounded-[15px] "
                      max={100}
                      value={selectedLayer.horizontal}
                      onChange={(e) => updateLayer(selectedLayer.id, "horizontal", Number(e.target.value))}
                    />


                    <label className="font-semibold mt-3 font-poppins">
                      Vertical
                    </label>

                    <input
                      style={{ background: "#000" }}
                      type="range"
                      min={0}
                      max={100}
                      className="w-full -mt-2 rounded-[15px] "
                      value={selectedLayer.vertical}
                      onChange={(e) => updateLayer(selectedLayer.id, "vertical", Number(e.target.value))}
                    />


                    <div className="flex justify-end gap-2 mt-3">

                      <button
                        onClick={removeSelectedLayer}
                        disabled={textLayers.length <= 1} // Disable if only one layer is left.
                        className={`bg-red-600 font-poppins text-white px-4 py-2 h-[40px] rounded-[10px]
    ${textLayers.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Remove
                      </button>
                    </div>
                    <button
                      onClick={handleDownload}
                      className="bg-black text-white mt-1  font-poppins flex justify-center items-center hover:bg-gray-800 h-[40px] py-2 rounded-[10px]"
                    >
                      <FiDownload color="white" className="mr-2" />   Download Image
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'image' && (
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => rotateImage('negative')}
                      className="border-[1.5px] border-[#e4e4e7] px-4 py-3 rounded-[14px]"
                    >
                      <img src={resetleft} className="w-4" />
                    </button>
                    <span className="text-center w-16 font-bold text-[25px]">{imageRotation}°</span>
                    <button
                      onClick={() => rotateImage('positive')}
                      className="border-[1.5px] border-[#e4e4e7] px-4 py-3 rounded-[14px]"
                    >
                      <img src={reset} className="w-4" />
                    </button>
                  </div>

                  <label className="font-semibold mt-3 font-poppins">
                    Brightness
                  </label>
                  <input
                    type="range"
                    style={{ background: "#000" }}
                    min={0.2}
                    max={2}
                    step={0.01}
                    value={brightness}
                    className="w-full -mt-2 rounded-[15px] "
                    onChange={(e) => setBrightness(Number(e.target.value))}
                  />


                  <label className="font-semibold mt-3 font-poppins">
                    Contrast
                  </label>
                  <input
                    type="range"
                    style={{ background: "#000" }}
                    min={0.2}
                    max={2}
                    step={0.01}
                    value={contrast}
                    className="w-full -mt-2 rounded-[15px] "
                    onChange={(e) => setContrast(Number(e.target.value))}
                  />


                  <button
                    onClick={enhanceImage}
                    className="bg-black font-poppins text-white mt-5 hover:bg-gray-800 h-[40px] py-2 rounded-[10px]"
                  >
                    Enhance Image
                  </button>

                  <button
                    onClick={handleDownload}
                    className="bg-black text-white mt-1 flex justify-center items-center hover:bg-gray-800 h-[40px] py-2 rounded-[10px]"
                  >
                    <FiDownload color="white" className="mr-2 font-poppins" />   Download Image
                  </button>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="flex flex-col gap-4 mt-4">
                  <label className="font-semibold font-poppins">
                    Aspect Ratio
                  </label>
                  <select
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    className="w-full border-2 font-poppins -mt-2 border-[#e7e7ea] px-2 rounded-[10px] p-2"
                  >
                    <option value="original">original</option>
                    <option value="4:3">4:3</option>
                    <option value="1:1">1:1 (Square)</option>
                    <option value="16:9">16:9</option>
                  </select>


                  <button
                    onClick={fixContentPositions}
                    className="bg-black font-poppins text-white mt-4 hover:bg-gray-800 h-[40px] py-2 rounded-[10px]"
                  >
                    Fix Content Position
                  </button>

                  <button
                    onClick={handleDownload}
                    className="bg-black text-white mt-1 flex justify-center items-center hover:bg-gray-800 h-[40px] py-2 rounded-[10px]"
                  >
                    <FiDownload color="white" className="mr-2 font-poppins" />   Download Image
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div >
      <div ><FourStepDesign /></div>
      <div><FeaturedSection /></div>
      <Footer />
    </>
  );
};

export default TextBehind;
